import React, {useCallback, useEffect, useMemo, useState} from 'react';
import useAuth from "../../../hooks/useAuth";
import {executeQueryUtil, executeQueryUtilWithCacheClear} from "../../../utils/gqlUtil";
import {getChartImplementationQuery} from "../../../gql/chartImplementationGql";
import localStrings from "../../../localStrings";
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Chip,
    CircularProgress,
    Divider,
    Grid,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import {MultiSelect} from "../../../components/multi-select";
import ApexChart from "react-apexcharts";
import {getChartDataQuery} from "../../../gql/chartDataGql";
import {applyFormatterKeys, getChartData} from "./ViewChartImplementation";
import CenterMessage from "../../../components/messages/CenterMessage";

import { useTheme } from '@mui/material/styles'; // en haut du fichier



const groupOptions = [
    {
        label: localStrings.noGroupBy,
        value: 'noGroupBy'
    },
    {
        label: localStrings.groupByCategory,
        value: 'category'
    },
    {
        label: localStrings.groupByStaff,
        value: 'staff'
    },
    {
        label: localStrings.groupByLocation,
        value: 'location'
    },
    {
        label: localStrings.groupByEstablishment,
        value: 'establishment'
    }
];

function ChartViewer({chartImplementationId, setLogs}) {
    const theme = useTheme(); // à mettre dans le composant
    const {
        currentBrand,
        currentEstablishment,
        filter,
        categories,
        establishments,
        locations,
        staffs,
        products,
    } = useAuth();

    const [chartImplementation, setChartImplementation] = useState(null);
    const [loading, setLoading] = useState(false);
    const [chips, setChips] = useState([]);
    const [categoriesOptions, setCategoriesOptions] = useState([]);
    const [establismentsOptions, setEstablismentsOptions] = useState([]);
    const [locationsOptions, setLocationsOptions] = useState([]);
    const [productOptions, setProductOptions] = useState([]);
    const [staffOptions, setStaffOptions] = useState([]);
    const [groupBy, setGroupBy] = useState("noGroupBy");
    const [chartData, setChartData] = useState(null);
    const [refresh, setRefresh] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {

        if (!chartImplementationId) {
            return;
        }
        const loadData = async () => {
            const res = await executeQueryUtil(getChartImplementationQuery(chartImplementationId
            ));
            setChartImplementation(res?.data?.getChartImplementation);
        }

        loadData();
    }, [chartImplementationId])

    useEffect(() => {

            setCategoriesOptions((categories || []).map((item, key) => {
                return {
                    label: item.name,
                    value: item.name
                }
            }));

            setEstablismentsOptions((establishments || []).map((item, key) => {
                return {
                    label: item.name,
                    value: item.name
                }
            }));

            setLocationsOptions((locations || []).map((item, key) => {
                return {
                    label: item.name,
                    value: item.name
                }
            }));

            setProductOptions((products || []).map((item, key) => {
                return {
                    label: item.name,
                    value: item.name
                }
            }));

            setStaffOptions((staffs || []).map((item, key) => {
                return {
                    label: item.name,
                    value: item.name
                }
            }));

        },
        [locations, categories, establishments, products, staffs])

    const loadChartImplementation = async (clearCache = false) => {

        try {
            setLoading(true);
            let resGraphData;
            if (clearCache) {
                resGraphData = await executeQueryUtilWithCacheClear(getChartDataQuery(
                    currentBrand().id, "", chartImplementationId, getChartData(filter, chips, groupBy)
                ));
            }
            else {
                resGraphData = await executeQueryUtil(getChartDataQuery(
                    currentBrand().id, "", chartImplementationId, getChartData(filter, chips, groupBy)
                ));
            }

            if (resGraphData?.data?.getChartData?.error) {
                setError(resGraphData.data?.getChartData?.error);
                setChartData(null);
                return
            }
            if (resGraphData?.data?.getChartData) {
                setChartData(resGraphData.data?.getChartData);
                setError(null);
            }

            if (resGraphData?.data?.getChartData?.logs && setLogs) {
                setLogs(resGraphData.data?.getChartData.logs.join('\r\n'));
            }
        }
        finally {
            setLoading(false);
        }

    }

    useEffect(() => {
        if (!chartImplementationId || !currentBrand() || !currentEstablishment()) {
            return;
        }
        if (!filter?.singleDay) {
            return;
        }

        loadChartImplementation(false);
    }, [chartImplementationId, currentBrand(), currentEstablishment(), filter, chips, groupBy])

    useEffect(() => {
        if (!chartImplementationId || !currentBrand() || !currentEstablishment()) {
            return;
        }
        if (!filter?.singleDay) {
            return;
        }

        loadChartImplementation(true);
    }, [refresh])

    const showChips = chips.length > 0;

    const handleChipDelete = useCallback((deletedChip) => {
        setChips((prevChips) => {
            return prevChips.filter((chip) => {
                // There can exist multiple chips for the same field.
                // Filter them by value.

                return !(deletedChip.field === chip.field && deletedChip.value === chip.value);
            });
        });
    }, []);

    const categoryValues = useMemo(() => chips
        .filter((chip) => chip.field === 'category')
        .map((chip) => chip.value), [chips]);

    const establishmentValues = useMemo(() => chips
        .filter((chip) => chip.field === 'establishment')
        .map((chip) => chip.value), [chips]);

    const locationValues = useMemo(() => chips
        .filter((chip) => chip.field === 'location')
        .map((chip) => chip.value), [chips]);

    const staffValues = useMemo(() => chips
        .filter((chip) => chip.field === 'staff')
        .map((chip) => chip.value), [chips]);

    const productValues = useMemo(() => chips
        .filter((chip) => chip.field === 'product')
        .map((chip) => chip.value), [chips]);

    const handleCategoryChange = useCallback((values) => {
        setChips((prevChips) => {
            const valuesFound = [];

            // First cleanup the previous chips
            const newChips = prevChips.filter((chip) => {
                if (chip.field !== 'category') {
                    return true;
                }

                const found = values.includes(chip.value);

                if (found) {
                    valuesFound.push(chip.value);
                }

                return found;
            });

            // Nothing changed
            if (values.length === valuesFound.length) {
                return newChips;
            }

            values.forEach((value) => {
                if (!valuesFound.includes(value)) {
                    const option = (categoriesOptions || [])
                        .find((option) => option.value === value);
                    if (option) {
                        newChips.push({
                            label: localStrings.category,
                            field: 'category',
                            value,
                            displayValue: option.label
                        });
                    }
                }
            });

            return newChips;
        });
    }, [categoriesOptions]);

    const handleEstablishmentChange = useCallback((values) => {
        setChips((prevChips) => {
            const valuesFound = [];

            // First cleanup the previous chips
            const newChips = prevChips.filter((chip) => {
                if (chip.field !== 'establishment') {
                    return true;
                }

                const found = values.includes(chip.value);

                if (found) {
                    valuesFound.push(chip.value);
                }

                return found;
            });

            // Nothing changed
            if (values.length === valuesFound.length) {
                return newChips;
            }

            values.forEach((value) => {
                if (!valuesFound.includes(value)) {
                    const option = (establismentsOptions || [])
                        .find((option) => option.value === value);
                    if (option) {
                        newChips.push({
                            label: localStrings.establishment,
                            field: 'establishment',
                            value,
                            displayValue: option.label
                        });
                    }
                }
            });

            return newChips;
        });
    }, [establismentsOptions]);

    const handleLocationChange = useCallback((values) => {
        setChips((prevChips) => {
            const valuesFound = [];

            // First cleanup the previous chips
            const newChips = prevChips.filter((chip) => {
                if (chip.field !== 'location') {
                    return true;
                }

                const found = values.includes(chip.value);

                if (found) {
                    valuesFound.push(chip.value);
                }

                return found;
            });

            // Nothing changed
            if (values.length === valuesFound.length) {
                return newChips;
            }

            values.forEach((value) => {
                if (!valuesFound.includes(value)) {
                    const option = (locationsOptions || [])
                        .find((option) => option.value === value);

                    if (option) {
                        newChips.push({
                            label: localStrings.location,
                            field: 'location',
                            value,
                            displayValue: option.label
                        });
                    }
                }
            });

            return newChips;
        });
    }, [locationsOptions]);


    const handleStaffChange = useCallback((values) => {
        setChips((prevChips) => {
            const valuesFound = [];

            // First cleanup the previous chips
            const newChips = prevChips.filter((chip) => {
                if (chip.field !== 'staff') {
                    return true;
                }

                const found = values.includes(chip.value);

                if (found) {
                    valuesFound.push(chip.value);
                }

                return found;
            });

            // Nothing changed
            if (values.length === valuesFound.length) {
                return newChips;
            }

            values.forEach((value) => {
                if (!valuesFound.includes(value)) {
                    const option = (staffOptions  || [])
                        .find((option) => option.value === value);
                    if (option) {
                        newChips.push({
                            label: localStrings.staff,
                            field: 'staff',
                            value,
                            displayValue: option.label
                        });
                    }
                }
            });

            return newChips;
        });
    }, [staffOptions]);


    const handleProductChange = useCallback((values) => {
        setChips((prevChips) => {
            const valuesFound = [];

            // First cleanup the previous chips
            const newChips = prevChips.filter((chip) => {
                if (chip.field !== 'product') {
                    return true;
                }

                const found = values.includes(chip.value);

                if (found) {
                    valuesFound.push(chip.value);
                }

                return found;
            });

            // Nothing changed
            if (values.length === valuesFound.length) {
                return newChips;
            }

            values.forEach((value) => {
                if (!valuesFound.includes(value)) {
                    const option = (productOptions  || [])
                        .find((option) => option.value === value);
                    if (option) {
                        newChips.push({
                            label: localStrings.product,
                            field: 'product',
                            value,
                            displayValue: option.label
                        });
                    }
                }
            });

            return newChips;
        });
    }, [productOptions]);

    function isSeriePresent() {
        const series = JSON.parse(chartData?.series || "[]");

        if (!series) return false;

        // Format pie/donut: array of numbers
        if (Array.isArray(series) && typeof series[0] === 'number') {
            return series.length > 0 && series.some(val => val !== 0 && val != null);
        }

        // Format bar/line: array of { name, data }
        if (Array.isArray(series)) {
            return series.some(s => Array.isArray(s.data) && s.data.some(val => val !== 0 && val != null));
        }

        return false;
    }


    function atLeastOneFiltersApplied() {
        return chartImplementation?.allowedEstablishmentFilter ||
            chartImplementation?.allowedCategoryFilter ||
            chartImplementation?.allowedLocationFilter ||
            chartImplementation?.allowedStaffFilter ||
            chartImplementation?.allowedProductFilter;
    }

    function atLeastOneGroupApplied() {
        return chartImplementation?.allowedGroupByLocation ||
            chartImplementation?.allowedGroupByStaff ||
            chartImplementation?.allowedGroupByCategory;
    }


    return (
        <Card
            sx={{
                borderRadius: '5px',
            }}

        >
            <CardHeader
                titleTypographyProps={{
                    variant: 'h6',
                    sx: {
                        color: theme.palette.primary.contrastText,
                        fontWeight: 600,
                        marginTop: '4px',
                        marginBottom: '4px',
                    },
                }}
                sx={{
                    backgroundColor: theme.palette.primary.main,
                    py: 1,
                    px: 2,
                    borderRadius: '5px',
                }}
                title={chartImplementation?.title || ""}
            />
            {!loading &&
                <div>

                    <Grid
                        container
                    >
                        {atLeastOneFiltersApplied() &&

                            <Grid
                                item
                                md={atLeastOneGroupApplied() ? 9 : 12}
                                xs={atLeastOneGroupApplied() ? 9 : 12}
                            >
                                {showChips
                                    ? (
                                        <Stack
                                            alignItems="center"
                                            direction="row"
                                            flexWrap="wrap"
                                            gap={1}
                                            sx={{p: 2}}
                                        >
                                            {chips.map((chip, index) => (
                                                <Chip
                                                    key={index}
                                                    label={(
                                                        <Box
                                                            sx={{
                                                                alignItems: 'center',
                                                                display: 'flex',
                                                                '& span': {
                                                                    fontWeight: 600
                                                                }
                                                            }}
                                                        >
                                                            <>
                                                        <span>
                                                          {chip.label}
                                                        </span>
                                                                :
                                                                {' '}
                                                                {chip.displayValue || chip.value}
                                                            </>
                                                        </Box>
                                                    )}
                                                    onDelete={() => handleChipDelete(chip)}
                                                    variant="outlined"
                                                />
                                            ))}
                                        </Stack>
                                    )
                                    : (
                                        <Box sx={{p: 2.5}}>
                                            <Typography
                                                color="text.secondary"
                                                variant="subtitle2"
                                            >
                                                {localStrings.noFilterApplied}
                                            </Typography>
                                        </Box>
                                    )}
                                <Divider/>

                                {/*<p>STACKKKK </p>*/}

                                <Stack
                                    alignItems="center"
                                    direction="row"
                                    flexWrap="wrap"
                                    spacing={1}
                                    sx={{p: 1}}
                                >
                                    {chartImplementation?.allowedCategoryFilter &&
                                        <MultiSelect
                                            label={localStrings.category}
                                            onChange={handleCategoryChange}
                                            options={categoriesOptions}
                                            value={categoryValues}
                                        />
                                    }

                                    {chartImplementation?.allowedEstablishmentFilter &&
                                        <MultiSelect
                                            label={localStrings.establishment}
                                            onChange={handleEstablishmentChange}
                                            options={establismentsOptions}
                                            value={establishmentValues}
                                        />
                                    }

                                    {chartImplementation?.allowedLocationFilter &&
                                        <MultiSelect
                                            label={localStrings.location}
                                            onChange={handleLocationChange}
                                            options={locationsOptions}
                                            value={locationValues}
                                        />
                                    }

                                    {chartImplementation?.allowedStaffFilter &&
                                        <MultiSelect
                                            label={localStrings.staff}
                                            onChange={handleStaffChange}
                                            options={staffOptions}
                                            value={staffValues}
                                        />
                                    }

                                    {chartImplementation?.allowedProductFilter &&
                                        <MultiSelect
                                            label={localStrings.product}
                                            onChange={handleProductChange}
                                            options={productOptions}
                                            value={productValues}
                                        />
                                    }

                                </Stack>
                            </Grid>
                        }

                        {atLeastOneGroupApplied() &&
                            <Grid
                                item
                                md={3}
                                xs={3}
                            >
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'flex-end',
                                    marginRight: '10px',
                                }}>
                                    <TextField
                                        label={localStrings.grouping}
                                        name="sort"
                                        onChange={(event) => {
                                            setGroupBy(event.target.value)
                                        }}
                                        select
                                        SelectProps={{native: true}}
                                        value={groupBy}
                                    >
                                        {groupOptions.map((option) => (
                                            <option
                                                key={option.value}
                                                value={option.value}
                                            >
                                                {option.label}
                                            </option>
                                        ))}
                                    </TextField>
                                </Box>
                            </Grid>
                        }
                    </Grid>
                </div>
            }

            <CardContent>

                {loading &&
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '350px'
                    }}>
                        <CircularProgress size="100px" />
                    </Box>
                }

                {!loading &&  chartData && !error && isSeriePresent() && chartData?.optionsJsCode &&
                    <ApexChart
                        options={applyFormatterKeys(JSON.parse(chartData?.optionsJsCode))}
                        series={JSON.parse(chartData.series)}
                        type={JSON.parse(chartData.optionsJsCode).chart?.type}
                        // type="bar"
                        height={chartImplementation?.height || 350}
                    />
                }

                {/*{!loading && chartData && !error && chartData.series && isSeriePresent() &&*/}
                {/*    <ApexChart*/}
                {/*        options={chartData.options}*/}
                {/*        series={chartData.series}*/}
                {/*        type={chartData.options.chart?.type}*/}
                {/*        height={chartImplementation?.height || 350}*/}
                {/*    />*/}
                {/*}*/}

                {!loading && chartData && !error && !isSeriePresent()  &&
                    <CenterMessage
                        height={chartImplementation?.height || 350}
                        title={localStrings.noDataInChart}
                    />

                }
            </CardContent>
        </Card>
    )
}

export default ChartViewer